import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete } from "@mui/material";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import useBuscaSelecaoMultipla from "../../hooks/useBuscaSelecaoMultipla";

const TicketsFilialSelect = ({ selectedFilialIds, onChange, filiais: filiaisProp }) => {
	const safeSelected = Array.isArray(selectedFilialIds) ? selectedFilialIds : [];
	const [filiaisState, setFiliaisState] = useState([]);
	const filiais = filiaisProp || filiaisState;

	useEffect(() => {
		if (filiaisProp) return;
		let active = true;
		const fetch = async () => {
			try {
				const { data } = await api.get("/filiais-distinct");
				if (active) {
					setFiliaisState(Array.isArray(data) ? data : []);
				}
			} catch (err) {
				toastError(err);
			}
		};
		fetch();
		return () => {
			active = false;
		};
	}, [filiaisProp]);

	const selected = useMemo(
		() => filiais.filter((f) => safeSelected.includes(f.id)),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[filiais, selectedFilialIds]
	);

	// Mantém o texto da busca ao marcar itens (o MUI limpa por padrão). Fica
	// antes do return antecipado abaixo: hook não pode ser condicional.
	const busca = useBuscaSelecaoMultipla();

	// Fail-closed: sem filiais (empresa filial / comum / sem filialIcode / erro),
	// o filtro não é exibido — some de fato, não fica vazio "para constar".
	if (!Array.isArray(filiais) || filiais.length === 0) {
		return null;
	}

	const handleChange = (event, newValue) => {
		if (newValue.some((option) => option.id === "toggle-all")) {
			if (safeSelected.length === filiais.length) {
				onChange([]);
			} else {
				onChange(filiais.map((f) => f.id));
			}
			return;
		}
		onChange(newValue.map((f) => f.id));
	};

	return (
		<Autocomplete
			multiple
			options={[{ id: "toggle-all", nome: "Selecionar Tudo" }, ...filiais]}
			getOptionLabel={(option) => option.nome || ""}
			value={selected}
			disableCloseOnSelect
			{...busca}
			onChange={handleChange}
			isOptionEqualToValue={(option, value) => option.id === value.id}
			filterOptions={(options, state) => {
				const input = (state.inputValue || "").toLowerCase().trim();
				if (!input) return options;
				return options.filter((option) =>
					option.id === "toggle-all" ||
					(option.nome || "").toLowerCase().includes(input)
				);
			}}
			ListboxProps={{
				style: {
					maxHeight: 300,
					overflowY: "auto",
					fontSize: "0.85rem",
				},
			}}
			sx={{
				fontSize: "0.85rem",
				"& .MuiAutocomplete-inputRoot": {
					padding: "4px !important",
					fontSize: "0.85rem",
				},
			}}
			renderTags={() => null}
			renderOption={(props, option, { selected: isSelected }) => (
				<MenuItem
					{...props}
					dense
					sx={{
						fontSize: "0.85rem",
						minHeight: "32px",
						paddingY: 0.5,
					}}
				>
					<Checkbox
						color="primary"
						checked={
							option.id === "toggle-all"
								? safeSelected.length === filiais.length && filiais.length > 0
								: isSelected
						}
						indeterminate={
							option.id === "toggle-all" &&
							safeSelected.length > 0 &&
							safeSelected.length < filiais.length
						}
						sx={{
							padding: "4px",
							"& svg": { fontSize: "18px" },
						}}
					/>
					<ListItemText
						primary={option.nome}
						primaryTypographyProps={{ fontSize: "0.85rem" }}
					/>
				</MenuItem>
			)}
			renderInput={(params) => (
				<TextField
					{...params}
					label="Filtrar por filial"
					variant="outlined"
					margin="dense"
					InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
					placeholder={safeSelected.length === 0 || safeSelected.length >= filiais.length
						? `Todas (${filiais.length})`
						: `${safeSelected.length} de ${filiais.length}`}
					sx={{
						"& input": { fontSize: "0.85rem" },
						"& label": { fontSize: "0.85rem" },
					}}
				/>
			)}
		/>
	);
};

export default TicketsFilialSelect;
